// Basic
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
// Libs
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Store & actions
import actionsPayluddSubscriptions from '../../api/actionsPayluddSubscriptions';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import EmergencyContractsTable from '../tables/EmergencyContractsTable';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import TransactionsTable from '@/app/modules/remoteCollections/components/tables/TransactionsTable';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import RenderInternationalizedAmount from '@/common-utils/components/renders/currency/RenderInternationalizedAmount';
import { Modal, ModalContent, ModalBody } from '@/common-utils/components/ui/modal';
// Common
import warningImg from 'url:../../../../../../../resource/images/warning.png';
import RouteUrls from '@/app/root/enums/RouteUrls';
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import { buildUrl, replaceParameters } from '@/common-utils/functions/restUtils';
import genUID from '@/common-utils/functions/generateUniqueId';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
// Lang
import { nsPayluddSubscription } from '@/app/i18n/namespaces';

/**
 *
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.posTerminalId
 * @returns ModalEmergencyRemoteCollection
 */
function ModalEmergencyRemoteCollection({ isOpen, setIsOpen, posTerminalId }) {

    // Toast
    const { toast } = useToast();

    // Constants
    const STEPS_NUMBER = 4;
    const STEP_TITLE_PREFIX = 'modal.emergency_remote_collection.steps';
    const CUSTOM_QUERY = {
        filters: [
            {
                key: 'eventType',
                value: 'IN_PROGRESS',
                hidden: true
            },
            {
                key: 'applications',
                value: 'CBEMV,CBCLESS',
                hidden: true
            },
            {
                key: 'emergencyRemoteCollectionStatus',
                value: 'EMPTY',
                hidden: true
            }
        ]
    };

    // State
    const [step, setStep] = useState(0);
    const [contractSelected, setContractSelected] = useState(getInitialValueForRowSelected());
    const [transactionsSelected, setTransactionsSelected] = useState(getInitialValueForRowSelected());
    const [summaryData, setSummaryData] = useState(null);

    // API Calls
    const {
        data: emergencyRemoteCollection,
        isLoading: isLoadingEmergencyRemoteCollection,
        error: errorEmergencyRemoteCollection
    } = actionsPayluddSubscriptions.getEmergencyRemoteCollection({ posTerminalId: posTerminalId });

    const onSummarizeSuccess = (data) => {
        setSummaryData({
            ...data.data,
            items: data.data.items.map(item => ({
                ...item,
                key: genUID()
            }))
        });
    };
    const onSummarizeError = () => { };
    const submitSummarizeEmergencyRemoteCollectionTransactions = actionsPayluddSubscriptions.summarizeEmergencyRemoteCollectionTransactions(
        onSummarizeSuccess,
        onSummarizeError
    );

    const onTriggerSuccess = () => {
        toast({
            description: <Entity entity={'modal.emergency_remote_collection.success'} namespace={nsPayluddSubscription} />,
            variant: 'success'
        });
        setIsOpen(false);
    };
    const onTriggerError = () => {
        toast({
            description: <Entity entity={'modal.emergency_remote_collection.error'} namespace={nsPayluddSubscription} />,
            variant: 'danger'
        });
        setIsOpen(false);
    };
    const submitTriggerEmergencyRemoteCollection = actionsPayluddSubscriptions.triggerEmergencyRemoteCollection(onTriggerSuccess, onTriggerError);

    // Hooks
    const { stepTitle, disabledNext, modalEnd } = useStepConfig(
        step,
        () => ({
            0: {
                stepTitle: `${STEP_TITLE_PREFIX}.warning`,
                disabledNext: false,
                modalEnd: false
            },
            1: {
                stepTitle: `${STEP_TITLE_PREFIX}.contract_selection`,
                disabledNext: !contractSelected.rowsData.length,
                modalEnd: !!errorEmergencyRemoteCollection || emergencyRemoteCollection?.totalResults === 0
            },
            2: {
                stepTitle: `${STEP_TITLE_PREFIX}.remote_collections_selection`,
                disabledNext: !transactionsSelected.rowsData.length,
                modalEnd: false
            },
            3: {
                stepTitle: `${STEP_TITLE_PREFIX}.summarize`,
                disabledNext: false,
                modalEnd: false
            }
        }),
        [contractSelected, transactionsSelected, errorEmergencyRemoteCollection, emergencyRemoteCollection]
    );

    useEffect(() => {
        if (step === 3) {
            submitSummarizeEmergencyRemoteCollectionTransactions.mutate(
                {
                    posTerminalId: posTerminalId,
                    transactionsList: transactionsSelected.rowIds
                }
            );
        }
    }, [step]);

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={cn(emergencyRemoteCollection && step > 0 ? 'max-w-4xl' : 'max-w-2xl')}>
                <HeaderTitle title={'modal.emergency_remote_collection.title'} namespace={nsPayluddSubscription} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsPayluddSubscription}
                        className={'mb-4 flex'}
                    />
                    {
                        step === 0 && (
                            <div className='flex flex-col gap-4 text-center'>
                                <img alt='' src={warningImg} className='w-10 h-10 self-center' />

                                <Entity className='font-bold' entity='modal.emergency_remote_collection.warning.subtitle' namespace={nsPayluddSubscription} />

                                <Entity entity='modal.emergency_remote_collection.warning.row1' namespace={nsPayluddSubscription} />

                                <Entity entity='modal.emergency_remote_collection.warning.row2' namespace={nsPayluddSubscription} />

                                <Entity className='text-red-500' entity='modal.emergency_remote_collection.warning.row3' namespace={nsPayluddSubscription} />

                            </div>
                        )
                    }
                    {
                        step === 1 && (
                            <div>
                                {
                                    isLoadingEmergencyRemoteCollection &&
                                    <Spinner className='mt-8' />
                                }
                                {
                                    errorEmergencyRemoteCollection &&
                                    <div className='text-center flex flex-col gap-4'>
                                        <img alt='' src={warningImg} className='w-10 h-10 self-center' />
                                        <Entity entity='modal.emergency_remote_collection.contract_selection.error' namespace={nsPayluddSubscription} />
                                    </div>
                                }
                                {
                                    emergencyRemoteCollection &&
                                    <div className='flex flex-col gap-4 text-center'>
                                        <Entity
                                            className='mb-4'
                                            entity='modal.emergency_remote_collection.contract_selection.instruction'
                                            data={{ label: emergencyRemoteCollection.label }}
                                            namespace={nsPayluddSubscription}
                                        />
                                        {
                                            emergencyRemoteCollection.totalResults !== 0 ?
                                                <EmergencyContractsTable
                                                    posTerminalId={posTerminalId}
                                                    singleSelection={true}
                                                    rowsSelected={contractSelected}
                                                    setRowsSelected={setContractSelected}
                                                />

                                                :
                                                <Link to={
                                                    buildUrl(
                                                        replaceParameters(
                                                            RouteUrls.MERCHANT_ACCOUNT_DETAILS.url,
                                                            { contractAccessId: emergencyRemoteCollection.uuid }
                                                        ),
                                                        { key: 'openModal' })}>
                                                    <Entity
                                                        className='text-blue-600 dark:text-blue-500 hover:underline'
                                                        entity='modal.emergency_remote_collection.contract_selection.redirect'
                                                        namespace={nsPayluddSubscription} />
                                                </Link>
                                        }
                                    </div>
                                }
                            </div>
                        )
                    }
                    {
                        step === 2 && (
                            <TransactionsTable
                                filtersEventName={setFiltersEventName('transactions', true)}
                                posTerminalId={posTerminalId}
                                customQuery={CUSTOM_QUERY}
                                rowsSelected={transactionsSelected}
                                setRowsSelected={setTransactionsSelected}
                            />
                        )
                    }
                    {
                        step === 3 && (
                            <div>
                                <div className='modal-tlc-subtitle-4 text-red-500 mb-4 text-center'>
                                    <Entity
                                        namespace={nsPayluddSubscription}
                                        entity='modal.emergency_remote_collection.summarize.warning'
                                    />
                                </div>
                                {submitSummarizeEmergencyRemoteCollectionTransactions.isPending &&
                                    <Spinner className='mt-4' />
                                }
                                {!submitSummarizeEmergencyRemoteCollectionTransactions.isPending && summaryData &&
                                    (
                                        <div>
                                            <table className='modal-tlc-table text-center'>
                                                <thead>
                                                    <tr>
                                                        <th>
                                                            <Entity
                                                                namespace={nsPayluddSubscription}
                                                                entity='modal.emergency_remote_collection.summarize.table.col1'
                                                                data={{ rank: summaryData.items[0].contractData.rank }}
                                                            />
                                                        </th>
                                                        <th>
                                                            <Entity
                                                                namespace={nsPayluddSubscription}
                                                                entity='modal.emergency_remote_collection.summarize.table.col2'
                                                                data={{ count: summaryData.items[0].debitData.count }}
                                                            />
                                                        </th>
                                                        <th>
                                                            <Entity
                                                                namespace={nsPayluddSubscription}
                                                                entity='modal.emergency_remote_collection.summarize.table.col3'
                                                                data={{ amount: summaryData.items[0].debitData.totalAmount }}
                                                            />
                                                        </th>
                                                        <th>
                                                            <Entity
                                                                namespace={nsPayluddSubscription}
                                                                entity='modal.emergency_remote_collection.summarize.table.col4'
                                                                data={{ amount: summaryData.items[0].creditData.count }}
                                                            />
                                                        </th>
                                                        <th>
                                                            <Entity
                                                                namespace={nsPayluddSubscription}
                                                                entity='modal.emergency_remote_collection.summarize.table.col5'
                                                                data={{ amount: summaryData.items[0].creditData.totalAmount }}
                                                            />
                                                        </th>
                                                        <th>
                                                            <Entity
                                                                namespace={nsPayluddSubscription}
                                                                entity='modal.emergency_remote_collection.summarize.table.col6'
                                                                data={{ count: summaryData.items[0].cancellationData.count }}
                                                            />
                                                        </th>
                                                        <th>
                                                            <Entity
                                                                namespace={nsPayluddSubscription}
                                                                entity='modal.emergency_remote_collection.summarize.table.col7'
                                                                data={{ amount: summaryData.items[0].cancellationData.totalAmount }}
                                                            />
                                                        </th>
                                                        <th>
                                                            <Entity
                                                                namespace={nsPayluddSubscription}
                                                                entity='modal.emergency_remote_collection.summarize.table.col8'
                                                                data={{ amount: summaryData.items[0].totalAmount }}
                                                            />
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {summaryData.items.map((row) => (
                                                        <tr key={row.key} >
                                                            <td className='modal-tlc-step4-col1 min-w-20' style={{ paddingTop: '10px' }}>
                                                                <div>{row.contractData.application}</div>
                                                                <div>{row.contractData.rank}</div>
                                                            </td>
                                                            <td >{row.debitData.count}</td>
                                                            <td >{!row.debitData.totalAmount ? row.debitData.totalAmount :
                                                                <RenderInternationalizedAmount
                                                                    amount={row.debitData.totalAmount}
                                                                    currency={row.currency} />}
                                                            </td>
                                                            <td >{row.creditData.count}</td>
                                                            <td >{!row.creditData.totalAmount ? row.creditData.totalAmount :
                                                                <RenderInternationalizedAmount amount={row.creditData.totalAmount} currency={row.currency} />}</td>
                                                            <td >{row.cancellationData.count}</td>
                                                            <td >{!row.cancellationData.totalAmount ? row.cancellationData.totalAmount :
                                                                <RenderInternationalizedAmount
                                                                    amount={row.cancellationData.totalAmount}
                                                                    currency={row.currency} />}
                                                            </td>
                                                            <td >{!row.totalAmount ? row.totalAmount :
                                                                <RenderInternationalizedAmount
                                                                    amount={row.totalAmount}
                                                                    currency={row.currency} />}</td>
                                                        </tr>
                                                    ))}
                                                    <tr>
                                                        <td colSpan={7}></td>
                                                        <td colSpan={1}>
                                                            <div className='modal-tlc-step4'>
                                                                <Entity
                                                                    className={'font-bold text-lg'}
                                                                    namespace={nsPayluddSubscription}
                                                                    entity='modal.emergency_remote_collection.summarize.total'
                                                                    data={{ total: summaryData.overallAmounts.total }}
                                                                />
                                                                {Object.entries(summaryData.overallAmounts).map(([currency, amount]) => (
                                                                    <RenderInternationalizedAmount
                                                                        key={currency}
                                                                        amount={amount}
                                                                        currency={currency}
                                                                    />
                                                                ))
                                                                }
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    )
                                }
                            </div>
                        )
                    }
                </ModalBody>
                {
                    <FooterStep
                        onValidate={() => {
                            submitTriggerEmergencyRemoteCollection.mutate({
                                posTerminalId: posTerminalId,
                                contractId: contractSelected.rowsData[0].uuid,
                                transactionsList: Object.keys(transactionsSelected.rowIds)
                            });
                        }}
                        step={step}
                        setStep={setStep}
                        stepsNumber={STEPS_NUMBER}
                        isLoading={submitTriggerEmergencyRemoteCollection.isLoading || submitSummarizeEmergencyRemoteCollectionTransactions.isLoading}
                        disableValidation={disabledNext}
                        isError={modalEnd}
                    />
                }
            </ModalContent>;
        </Modal >
    );
}

ModalEmergencyRemoteCollection.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    posTerminalId: PropTypes.string.isRequired
};


export default ModalEmergencyRemoteCollection;